import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrainCircuit, Plus, ChevronRight, CircleAlert as AlertCircle } from 'lucide-react';
import { listOpportunities } from '../../lib/mavisApi';
import { useAuth } from '../../contexts/AuthContext';
import type { Account } from '../../types';
import type { MavisOpportunity } from '../../types/mavis';
import LoadingSpinner from '../common/LoadingSpinner';
import MavisStageChip from './MavisStageChip';
import MavisForecastLabelChip from './MavisForecastLabelChip';
import MavisCreateOpportunityModal from './MavisCreateOpportunityModal';

interface MavisAccountTabProps {
  account: Account;
  canEdit: boolean;
}

function formatAmount(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUpdated(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function MavisAccountTab({ account, canEdit }: MavisAccountTabProps) {
  const navigate = useNavigate();
  const { organisation } = useAuth();
  const [opportunities, setOpportunities] = useState<MavisOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadOpportunities = useCallback(async () => {
    if (!organisation) return;
    setLoading(true);
    setError(null);

    try {
      const { opportunities: data } = await listOpportunities(organisation.id, account.id);
      setOpportunities(data as unknown as MavisOpportunity[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load opportunities. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [organisation, account.id]);

  useEffect(() => { loadOpportunities(); }, [loadOpportunities]);

  const handleCreated = async (id: string) => {
    setShowCreateModal(false);
    await loadOpportunities();
    navigate(`/app/mavis/${id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-app-ink">MAVIS Opportunities</h2>
          <p className="text-sm text-app-muted mt-0.5">
            Deal pipeline and qualification for {account.company_name}
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            New Opportunity
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button
            onClick={loadOpportunities}
            className="text-sm text-red-600 hover:underline whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table card */}
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : opportunities.length === 0 ? (
          <div className="py-16 text-center px-6">
            <BrainCircuit className="w-10 h-10 text-app-muted mx-auto mb-4" />
            <p className="text-sm font-medium text-app-muted mb-1">
              No MAVIS opportunities for this account yet
            </p>
            <p className="text-xs text-app-muted max-w-sm mx-auto mb-6">
              Track deals through MEDDPICC qualification, manage stakeholders, and get AI-powered
              forecast assistance — all scoped to {account.company_name}.
            </p>
            {canEdit && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary !text-sm !py-2 !px-4"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Create First Opportunity
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-app-line bg-app-canvas/60">
                    {['Opportunity', 'Stage', 'Amount', 'Close Date', 'Forecast', 'Fcst %', 'Updated'].map(col => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-[11px] font-semibold text-app-muted uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map(opp => (
                    <tr
                      key={opp.id}
                      onClick={() => navigate(`/app/mavis/${opp.id}`)}
                      className="border-b border-app-line last:border-b-0 hover:bg-forest-50/40 cursor-pointer transition-colors group"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-app-ink max-w-[240px] truncate">
                        {opp.title}
                      </td>
                      <td className="px-4 py-3">
                        <MavisStageChip stage={opp.stage} />
                      </td>
                      <td className="px-4 py-3 text-sm text-app-ink tabular-nums whitespace-nowrap">
                        {formatAmount(opp.amount, opp.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-app-muted whitespace-nowrap">
                        {formatDate(opp.target_close_date)}
                      </td>
                      <td className="px-4 py-3">
                        {opp.forecast_label
                          ? <MavisForecastLabelChip label={opp.forecast_label} />
                          : <span className="text-app-muted text-sm">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-sm text-app-muted tabular-nums">
                        {opp.forecast_percentage !== null ? `${opp.forecast_percentage}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-app-muted whitespace-nowrap">
                        {formatUpdated(opp.updated_at)}
                      </td>
                      <td className="px-3 py-3 text-app-muted group-hover:text-app-muted transition-colors">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t border-app-line bg-app-canvas/40">
              <p className="text-xs text-app-muted">
                {opportunities.length} {opportunities.length === 1 ? 'opportunity' : 'opportunities'}
              </p>
            </div>
          </>
        )}
      </div>

      <MavisCreateOpportunityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        defaultAccountId={account.id}
        defaultAccountName={account.company_name}
        lockAccount
      />
    </div>
  );
}
